"use client";

import Link from "next/link";
import { motion } from "framer-motion";
import { FadeUp } from "@/components/ui/section";

const BULLETS = [
  {
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
      </svg>
    ),
    text: "Live in 2 weeks — trained on your services and tone",
  },
  {
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
      </svg>
    ),
    text: "24/7 call coverage — no hold music, no voicemail",
  },
  {
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244" />
      </svg>
    ),
    text: "Integrates with your existing scheduling system",
  },
];

const TRANSCRIPT = [
  { role: "caller", text: "Hi, I need to book an appointment for Thursday." },
  { role: "agent", text: "Of course! Dr. Chen has Thursday at 10 AM or 2 PM. Which works better?" },
  { role: "caller", text: "10 AM please." },
  { role: "agent", text: "Done — Thursday at 10 AM. Confirmation text sent." },
];

function AgentBubble({ text, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -8 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.35, delay }}
      className="flex items-start gap-2"
    >
      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center text-white text-[9px] font-bold flex-shrink-0 mt-0.5">
        AI
      </div>
      <div className="bg-violet-500/15 border border-violet-500/20 rounded-2xl rounded-tl-none px-3 py-2 text-xs text-zinc-200 leading-relaxed max-w-[85%]">
        {text}
      </div>
    </motion.div>
  );
}

function CallerBubble({ text, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 8 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.35, delay }}
      className="flex items-start gap-2 flex-row-reverse"
    >
      <div className="w-6 h-6 rounded-full bg-zinc-700 border border-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-3 h-3 text-zinc-400">
          <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" />
        </svg>
      </div>
      <div className="bg-zinc-800 border border-white/5 rounded-2xl rounded-tr-none px-3 py-2 text-xs text-zinc-300 leading-relaxed max-w-[85%]">
        {text}
      </div>
    </motion.div>
  );
}

export default function HomeVoiceAgent() {
  return (
    <section className="py-24 lg:py-32 bg-zinc-900/40 border-t border-b border-white/[0.04] relative overflow-hidden">
      {/* Subtle background glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-1/4 w-[500px] h-[300px] bg-[radial-gradient(ellipse,rgba(124,58,237,0.07),transparent_70%)]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">

          {/* Left: copy */}
          <FadeUp>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-500/10 border border-violet-500/20 text-violet-300 text-xs font-semibold uppercase tracking-widest mb-6">
              <motion.span
                className="w-1.5 h-1.5 rounded-full bg-violet-400 inline-block"
                animate={{ opacity: [1, 0.3, 1] }}
                transition={{ duration: 1.8, repeat: Infinity }}
              />
              Featured Product
            </div>

            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4 leading-tight">
              Your business never misses{" "}
              <span className="bg-gradient-to-r from-violet-400 to-cyan-400 bg-clip-text text-transparent">
                another call
              </span>
            </h2>

            <p className="text-zinc-400 text-lg leading-relaxed mb-8">
              AI Voice Agents for service businesses — handling inbound calls
              around the clock, booking appointments, and routing callers
              without a human on the line.
            </p>

            <ul className="space-y-3 mb-10">
              {BULLETS.map((b) => (
                <li key={b.text} className="flex items-center gap-3 text-sm text-zinc-300">
                  <span className="w-7 h-7 rounded-lg bg-violet-500/10 border border-violet-500/20 text-violet-400 flex items-center justify-center flex-shrink-0">
                    {b.icon}
                  </span>
                  {b.text}
                </li>
              ))}
            </ul>

            <Link
              href="/ai-voice-agent"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-500 text-white text-sm font-semibold transition-all duration-200 shadow-lg shadow-violet-500/25 hover:shadow-violet-500/40"
            >
              See how it works
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
              </svg>
            </Link>
          </FadeUp>

          {/* Right: mini transcript */}
          <FadeUp delay={0.15}>
            <div className="rounded-2xl border border-white/[0.08] bg-zinc-900 overflow-hidden shadow-2xl shadow-black/40">

              {/* Window chrome */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06] bg-zinc-900/90">
                <div className="flex items-center gap-2.5">
                  <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
                  </div>
                  <span className="text-xs text-zinc-500 font-medium">Asynq Voice Agent · Medical Clinic</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <motion.span
                    className="w-1.5 h-1.5 rounded-full bg-green-400"
                    animate={{ opacity: [1, 0.4, 1] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  />
                  <span className="text-[10px] text-green-400 font-medium">Live</span>
                </div>
              </div>

              {/* Transcript */}
              <div className="p-5 space-y-3">
                {TRANSCRIPT.map((msg, i) =>
                  msg.role === "agent" ? (
                    <AgentBubble key={i} text={msg.text} delay={i * 0.1} />
                  ) : (
                    <CallerBubble key={i} text={msg.text} delay={i * 0.1} />
                  )
                )}
              </div>

              {/* Outcome footer */}
              <div className="px-5 py-3.5 border-t border-white/[0.06] flex items-center justify-between">
                <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400">
                  <svg className="w-3 h-3" viewBox="0 0 12 12" fill="currentColor">
                    <path fillRule="evenodd" d="M10.22 3.22a.75.75 0 0 1 0 1.06l-5.25 5.25a.75.75 0 0 1-1.06 0L1.78 7.4a.75.75 0 0 1 1.06-1.06l1.6 1.6 4.72-4.72a.75.75 0 0 1 1.06 0Z" clipRule="evenodd" />
                  </svg>
                  Appointment booked in 90 seconds
                </span>
                <span className="text-[10px] text-zinc-600">Response &lt; 800ms</span>
              </div>
            </div>

            {/* Stat pills below card */}
            <div className="flex gap-3 mt-4 flex-wrap">
              {["Medical & Dental", "Real Estate", "Law Firms", "Salons & Spas"].map((label) => (
                <span key={label} className="text-xs px-3 py-1 rounded-full bg-zinc-800/80 border border-white/[0.06] text-zinc-500">
                  {label}
                </span>
              ))}
            </div>
          </FadeUp>

        </div>
      </div>
    </section>
  );
}
